import React, { useMemo, useState, Children, Component } from 'react';
import { PageLayout } from '../shared/PageLayout';
import { StatCard } from '../shared/StatCard';
import { Badge } from '../../../ui/Badge';
import { Button } from '../../../ui/Button';
import { Card } from '../../../ui/Card';
import { Database, BookOpen, Eye, Layers, Image as ImageIcon, ChevronLeft, Search, MousePointer, Zap, Component as ComponentIcon, Hash, CheckCircle2, XCircle, ChevronDown, ChevronRight, Tag, MessageSquare, Ruler, PieChart, Grid3x3, ArrowLeftRight, Table, Lightbulb, Coins, Columns, Clock, Grid2x2, Scissors, SquareStack, Circle, Crosshair, Shapes, Calculator, PenTool, BarChart3, BoxIcon } from 'lucide-react';
import { motion } from 'framer-motion';
import { Question } from '../../../../types';
import { ALL_QUESTIONS_CORE, getQuestionWithExplanation, QUESTIONS_BY_FAMILY } from '../../../../data/questionLoader';
import { QUESTION_TYPE_REGISTRY } from '../../../../config/questionTypeRegistry';
import { VISUAL_REGISTRY } from '../../../../visuals/registry/visualRegistry';
import { QuestionCardClean } from '../../../practice/QuestionCardClean';
import { QuestionRendererFactory } from '../../../practice/renderers/QuestionRendererFactory';
interface Subject {
  code: string;
  name: string;
  count: number;
  familyCount: number;
  color: string;
  tierMin: number;
  tierMax: number;
}
interface QuestionBankPageProps {
  questions?: Question[];
}
const SUBJECT_META: Record<string, {
  name: string;
  color: string;
}> = {
  GET: {
    name: 'Getallen',
    color: 'bg-blue-500'
  },
  MET: {
    name: 'Meten',
    color: 'bg-green-500'
  },
  SPR: {
    name: 'Spelling',
    color: 'bg-purple-500'
  },
  SWW: {
    name: 'Werkwoorden',
    color: 'bg-indigo-500'
  },
  TAA: {
    name: 'Taal',
    color: 'bg-pink-500'
  },
  TGL: {
    name: 'Tijd & Geld',
    color: 'bg-orange-500'
  },
  TOPO: {
    name: 'Topografie',
    color: 'bg-teal-500'
  },
  STU: {
    name: 'Studievaardigheden',
    color: 'bg-cyan-500'
  }
};
const VISUAL_ICONS: Record<string, any> = {
  stairs: Ruler,
  fraction: PieChart,
  geo: Grid3x3,
  numberline: ArrowLeftRight,
  ratio: Table,
  rule: Lightbulb,
  money: Coins,
  column: Columns,
  clock: Clock,
  decimalgrid: Grid2x2,
  verbTable: BookOpen,
  kofschipMnemonic: Scissors,
  syllableBlocks: SquareStack,
  circle: Circle,
  coordinateGrid: Crosshair,
  box3d: BoxIcon,
  compositeShape: Shapes
};
const getTypeIcon = (type: string) => {
  if (['multiple_choice', 'cijferend_optellen', 'cijferend_aftrekken', 'number_sequence', 'ratio_table'].includes(type)) return Calculator;
  if (['fill_in', 'drag_drop_fill', 'matching', 'ordering', 'text_highlight'].includes(type)) return PenTool;
  if (['clock', 'schedule_reading'].includes(type)) return Clock;
  if (['money'].includes(type)) return Coins;
  if (['bar_chart', 'line_chart', 'pie_chart', 'table_reading'].includes(type)) return BarChart3;
  return Layers;
};
const containerVariants = {
  hidden: {
    opacity: 0
  },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.05
    }
  }
};
const itemVariants = {
  hidden: {
    opacity: 0,
    y: 20
  },
  show: {
    opacity: 1,
    y: 0,
    transition: {
      type: 'spring',
      stiffness: 300,
      damping: 24
    }
  }
};
type TabId = 'overview' | 'types' | 'visuals' | 'families';
export function QuestionBankPage({
  questions = []
}: QuestionBankPageProps) {
  const [activeTab, setActiveTab] = useState<TabId>('overview');
  const [selectedSubject, setSelectedSubject] = useState<string | null>(null);
  const [selectedQuestionId, setSelectedQuestionId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  // Types Tab State
  const [selectedType, setSelectedType] = useState<string | null>(null);
  const [typeSearchQuery, setTypeSearchQuery] = useState('');
  // Visuals Tab State
  const [selectedVisual, setSelectedVisual] = useState<string | null>(null);
  // Families Tab State
  const [familySearchQuery, setFamilySearchQuery] = useState('');
  const [expandedSubjects, setExpandedSubjects] = useState<Set<string>>(new Set(Object.keys(SUBJECT_META)));
  // Compute per-subject stats
  const subjects = useMemo((): Subject[] => {
    const source = ALL_QUESTIONS_CORE;
    const bySubject: Record<string, {
      count: number;
      families: Set<string>;
      tiers: number[];
    }> = {};
    source.forEach(q => {
      const code = q.familyId?.split('-')[0] || 'UNK';
      if (!bySubject[code]) {
        bySubject[code] = {
          count: 0,
          families: new Set(),
          tiers: []
        };
      }
      bySubject[code].count++;
      if (q.familyId) bySubject[code].families.add(q.familyId);
      if (q.tier) bySubject[code].tiers.push(q.tier);
    });
    return Object.entries(SUBJECT_META).map(([code, meta]) => {
      const data = bySubject[code];
      const tiers = data?.tiers ?? [];
      return {
        code,
        name: meta.name,
        count: data?.count ?? 0,
        familyCount: data?.families.size ?? 0,
        color: meta.color,
        tierMin: tiers.length ? Math.min(...tiers) : 0,
        tierMax: tiers.length ? Math.max(...tiers) : 0
      };
    });
  }, []);
  const totalQuestions = ALL_QUESTIONS_CORE.length || questions.length;
  const subjectCount = subjects.filter(s => s.count > 0).length;
  const typeCount = Object.keys(QUESTION_TYPE_REGISTRY).length;
  const visualCount = Object.keys(VISUAL_REGISTRY).length;
  const totalFamiliesCount = QUESTIONS_BY_FAMILY.size;
  // --- Subject Browser Logic ---
  const subjectQuestions = useMemo(() => {
    if (!selectedSubject) return [];
    return ALL_QUESTIONS_CORE.filter(q => q.familyId?.startsWith(selectedSubject));
  }, [selectedSubject]);
  const filteredQuestions = useMemo(() => {
    if (!searchQuery) return subjectQuestions;
    const lowerQuery = searchQuery.toLowerCase();
    return subjectQuestions.filter(q => q.question.toLowerCase().includes(lowerQuery) || q.id.toLowerCase().includes(lowerQuery));
  }, [subjectQuestions, searchQuery]);
  const fullSelectedQuestion = useMemo(() => {
    if (!selectedQuestionId) return null;
    return getQuestionWithExplanation(selectedQuestionId);
  }, [selectedQuestionId]);
  // --- Types Logic ---
  const typeCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    ALL_QUESTIONS_CORE.forEach(q => {
      counts[q.questionType] = (counts[q.questionType] || 0) + 1;
    });
    return counts;
  }, []);
  const typesList = useMemo(() => {
    return Object.entries(QUESTION_TYPE_REGISTRY).map(([type, config]) => ({
      type,
      ...config,
      count: typeCounts[type] || 0,
      usagePct: totalQuestions > 0 ? Math.round((typeCounts[type] || 0) / totalQuestions * 100) : 0
    })).sort((a, b) => b.count - a.count);
  }, [typeCounts, totalQuestions]);
  const filteredTypesList = useMemo(() => {
    if (!typeSearchQuery) return typesList;
    const lowerQuery = typeSearchQuery.toLowerCase();
    return typesList.filter(t => t.label.toLowerCase().includes(lowerQuery) || t.type.toLowerCase().includes(lowerQuery));
  }, [typesList, typeSearchQuery]);
  const typeQuestions = useMemo(() => {
    if (!selectedType) return [];
    return ALL_QUESTIONS_CORE.filter(q => q.questionType === selectedType);
  }, [selectedType]);
  const typeFamilies = useMemo(() => {
    if (!selectedType) return [];
    const families = new Set<string>();
    typeQuestions.forEach(q => {
      if (q.familyId) families.add(q.familyId);
    });
    return Array.from(families).sort();
  }, [typeQuestions, selectedType]);
  // --- Visuals Logic ---
  const registeredVisuals = useMemo(() => Object.keys(VISUAL_REGISTRY).sort(), []);
  const visualUsage = useMemo(() => {
    const usage: Record<string, {
      count: number;
      stimulusCount: number;
      supportCount: number;
      families: Set<string>;
      modules: Set<string>;
      questions: Question[];
    }> = {};
    registeredVisuals.forEach(v => {
      usage[v] = {
        count: 0,
        stimulusCount: 0,
        supportCount: 0,
        families: new Set(),
        modules: new Set(),
        questions: []
      };
    });
    ALL_QUESTIONS_CORE.forEach(q => {
      const moduleCode = q.familyId?.split('-')[0] || 'UNK';
      let usedInQuestion = false;
      q.stimulusAids?.forEach(aid => {
        if (aid.type && usage[aid.type]) {
          usage[aid.type].count++;
          usage[aid.type].stimulusCount++;
          usage[aid.type].families.add(q.familyId);
          usage[aid.type].modules.add(moduleCode);
          usedInQuestion = true;
        }
      });
      q.supportAids?.forEach(aid => {
        if (aid.type && usage[aid.type]) {
          usage[aid.type].count++;
          usage[aid.type].supportCount++;
          usage[aid.type].families.add(q.familyId);
          usage[aid.type].modules.add(moduleCode);
          usedInQuestion = true;
        }
      });
      if (usedInQuestion) {
        // Find all visuals used in this question and add the question to their list
        const usedTypes = new Set<string>();
        q.stimulusAids?.forEach(aid => aid.type && usedTypes.add(aid.type));
        q.supportAids?.forEach(aid => aid.type && usedTypes.add(aid.type));
        usedTypes.forEach(type => {
          if (usage[type]) {
            usage[type].questions.push(q);
          }
        });
      }
    });
    return usage;
  }, [registeredVisuals]);
  const maxVisualUsage = useMemo(() => {
    return Math.max(...Object.values(visualUsage).map(u => u.count), 1);
  }, [visualUsage]);
  const sortedVisuals = useMemo(() => {
    return [...registeredVisuals].sort((a, b) => visualUsage[b].count - visualUsage[a].count);
  }, [registeredVisuals, visualUsage]);
  // --- Families Logic ---
  const familiesBySubject = useMemo(() => {
    const grouped: Record<string, {
      id: string;
      questionCount: number;
      tiers: Set<number>;
      types: Set<string>;
    }[]> = {};
    Object.keys(SUBJECT_META).forEach(code => {
      grouped[code] = [];
    });
    Array.from(QUESTIONS_BY_FAMILY.entries()).forEach(([familyId, questions]) => {
      const code = familyId.split('-')[0] || 'UNK';
      if (!grouped[code]) grouped[code] = [];
      const tiers = new Set<number>();
      const types = new Set<string>();
      questions.forEach(q => {
        if (q.tier) tiers.add(q.tier);
        if (q.questionType) types.add(q.questionType);
      });
      grouped[code].push({
        id: familyId,
        questionCount: questions.length,
        tiers,
        types
      });
    });
    // Sort families within subjects
    Object.keys(grouped).forEach(code => {
      grouped[code].sort((a, b) => a.id.localeCompare(b.id));
    });
    return grouped;
  }, []);
  const toggleSubject = (code: string) => {
    const newExpanded = new Set(expandedSubjects);
    if (newExpanded.has(code)) {
      newExpanded.delete(code);
    } else {
      newExpanded.add(code);
    }
    setExpandedSubjects(newExpanded);
  };
  // --- Render Helpers ---
  const renderOverview = () => <div className="space-y-6" data-id="element-89">
      {/* Stat Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-90">
        <StatCard label="Totaal Vragen" value={totalQuestions} icon={<Database className="w-5 h-5" data-id="element-92" />} data-id="element-91" />
        <StatCard label="Families" value={totalFamiliesCount} icon={<Tag className="w-5 h-5" data-id="element-94" />} data-id="element-93" />
        <StatCard label="Vraagtypen" value={typeCount} icon={<Layers className="w-5 h-5" data-id="element-96" />} data-id="element-95" />
        <StatCard label="Visuele Componenten" value={visualCount} icon={<ComponentIcon className="w-5 h-5" data-id="element-98" />} data-id="element-97" />
      </div>

      <div className="flex items-center justify-between" data-id="element-99">
        <h3 className="text-lg font-semibold text-gray-900" data-id="element-100">
          Vakken — klik om vragen te bekijken
        </h3>
        <span className="text-sm text-gray-400" data-id="element-101">
          {totalQuestions} vragen verdeeld over {subjectCount} vakken
        </span>
      </div>

      {/* Subject Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-102">
        {subjects.map(subject => <Card key={subject.code} className="p-5 cursor-pointer hover:shadow-md transition-all hover:border-blue-300 border border-gray-200 flex flex-col" onClick={() => setSelectedSubject(subject.code)} data-id="element-103">
            <div className="flex items-center gap-3 mb-3" data-id="element-104">
              <div className={`w-3 h-3 rounded-full ${subject.color}`} data-id="element-105" />
              <h4 className="font-bold text-gray-900" data-id="element-106">{subject.name}</h4>
            </div>
            <div className="flex items-center justify-between mb-2" data-id="element-107">
              <span className="text-2xl font-black text-gray-800" data-id="element-108">
                {subject.count}
              </span>
              {subject.tierMin > 0 && <Badge variant="neutral" size="sm" data-id="element-109">
                  Tier {subject.tierMin}–{subject.tierMax}
                </Badge>}
            </div>
            <div className="mt-auto flex items-center justify-between text-xs text-gray-500" data-id="element-110">
              <span data-id="element-111">{subject.count} vragen</span>
              <span className="flex items-center gap-1" data-id="element-112">
                <Tag className="w-3 h-3" data-id="element-113" /> {subject.familyCount} families
              </span>
            </div>
          </Card>)}
      </div>

      {/* Quick tip */}
      <div className="rounded-lg border border-blue-100 bg-blue-50 px-4 py-3 text-sm text-blue-700" data-id="element-114">
        <strong data-id="element-115">Tip:</strong> Kwaliteitsscores en validatieproblemen zijn te
        vinden onder{' '}
        <span className="font-semibold" data-id="element-116">Kwaliteitsstatistieken</span>.
      </div>
    </div>;
  const renderSubjectBrowser = () => <div className="flex flex-col h-[calc(100vh-12rem)] bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm" data-id="element-117">
      <div className="flex items-center gap-4 p-4 border-b border-gray-200 bg-gray-50 flex-shrink-0" data-id="element-118">
        <Button variant="secondary" size="sm" onClick={() => {
        setSelectedSubject(null);
        setSelectedQuestionId(null);
        setSearchQuery('');
      }} data-id="element-119">
          <ChevronLeft className="w-4 h-4 mr-1" data-id="element-120" /> Terug naar overzicht
        </Button>
        <h2 className="text-lg font-bold text-gray-900" data-id="element-121">
          {SUBJECT_META[selectedSubject!]?.name || selectedSubject}{' '}
          <span className="text-gray-500 font-normal text-sm ml-2" data-id="element-122">
            ({subjectQuestions.length} vragen)
          </span>
        </h2>
      </div>
      <div className="flex flex-1 min-h-0" data-id="element-123">
        {/* Left pane: List */}
        <div className="w-1/3 border-r border-gray-200 flex flex-col bg-white" data-id="element-124">
          <div className="p-3 border-b border-gray-100 flex-shrink-0" data-id="element-125">
            <div className="relative" data-id="element-126">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" data-id="element-127" />
              <input type="text" placeholder="Zoek in vragen..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none" data-id="element-128" />
            </div>
          </div>
          <div className="flex-1 overflow-y-auto" data-id="element-129">
            {filteredQuestions.map(q => <button key={q.id} onClick={() => setSelectedQuestionId(q.id)} className={`w-full text-left p-4 border-b border-gray-100 hover:bg-gray-50 transition-colors ${selectedQuestionId === q.id ? 'bg-blue-50 border-l-4 border-l-blue-500' : 'border-l-4 border-l-transparent'}`} data-id="element-130">
                <div className="flex items-center gap-2 mb-1.5" data-id="element-131">
                  <span className="text-xs font-mono font-semibold text-gray-600 bg-gray-100 px-1.5 py-0.5 rounded" data-id="element-132">
                    {q.id}
                  </span>
                  <Badge variant="neutral" size="sm" data-id="element-133">
                    Tier {q.tier}
                  </Badge>
                </div>
                <p className="text-sm text-gray-800 line-clamp-2 leading-snug" data-id="element-134">
                  {q.question}
                </p>
              </button>)}
            {filteredQuestions.length === 0 && <div className="p-8 text-center text-gray-500 text-sm" data-id="element-135">
                Geen vragen gevonden voor deze zoekopdracht.
              </div>}
          </div>
        </div>
        {/* Right pane: Preview */}
        <div className="w-2/3 bg-slate-100 flex flex-col relative" data-id="element-136">
          {fullSelectedQuestion ? <div className="flex-1 overflow-y-auto p-6 md:p-8" data-id="element-137">
              <div className="max-w-3xl mx-auto" data-id="element-138">
                <div className="mb-4 flex items-center justify-between" data-id="element-139">
                  <div className="flex items-center gap-2" data-id="element-140">
                    <Badge variant="info" size="sm" data-id="element-141">
                      {fullSelectedQuestion.questionType}
                    </Badge>
                    <span className="text-xs text-gray-500 font-mono" data-id="element-142">
                      {fullSelectedQuestion.familyId}
                    </span>
                  </div>

                  {/* Visual Aids Indicator */}
                  <div className="flex items-center gap-3" data-id="element-143">
                    {((fullSelectedQuestion.stimulusAids?.length || 0) > 0 || (fullSelectedQuestion.supportAids?.length || 0) > 0) && <div className="flex items-center gap-1.5 text-xs font-medium text-purple-600 bg-purple-50 px-2 py-1 rounded-md border border-purple-100" data-id="element-144">
                        <ImageIcon className="w-3.5 h-3.5" data-id="element-145" />
                        {(fullSelectedQuestion.stimulusAids?.length || 0) + (fullSelectedQuestion.supportAids?.length || 0)}{' '}
                        visuals
                      </div>}
                  </div>
                </div>

                <QuestionCardClean question={fullSelectedQuestion} data-id="element-146">
                  <QuestionRendererFactory question={fullSelectedQuestion} onAnswer={() => {}} data-id="element-147" />
                </QuestionCardClean>

                {/* Developer Info Panel */}
                <div className="mt-8 bg-white rounded-xl border border-gray-200 p-5 shadow-sm" data-id="element-148">
                  <h4 className="text-sm font-bold text-gray-900 mb-4 flex items-center gap-2" data-id="element-149">
                    <Database className="w-4 h-4 text-gray-400" data-id="element-150" />
                    Vraag Data & Feedback
                  </h4>
                  <div className="grid grid-cols-2 gap-6 text-sm mb-6" data-id="element-151">
                    <div data-id="element-152">
                      <span className="block text-xs text-gray-500 mb-1" data-id="element-153">
                        Skill
                      </span>
                      <span className="font-medium text-gray-800" data-id="element-154">
                        {fullSelectedQuestion.skill}
                      </span>
                    </div>
                    <div data-id="element-155">
                      <span className="block text-xs text-gray-500 mb-1" data-id="element-156">
                        SubSkill
                      </span>
                      <span className="font-medium text-gray-800" data-id="element-157">
                        {fullSelectedQuestion.subSkill}
                      </span>
                    </div>
                    <div data-id="element-158">
                      <span className="block text-xs text-gray-500 mb-1" data-id="element-159">
                        Level Code
                      </span>
                      <span className="font-medium text-gray-800" data-id="element-160">
                        {fullSelectedQuestion.levelCode}
                      </span>
                    </div>
                    <div data-id="element-161">
                      <span className="block text-xs text-gray-500 mb-1" data-id="element-162">
                        Groep
                      </span>
                      <span className="font-medium text-gray-800" data-id="element-163">
                        Groep {fullSelectedQuestion.group}
                      </span>
                    </div>
                  </div>

                  <div className="border-t border-gray-100 pt-4" data-id="element-164">
                    <h5 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3 flex items-center gap-1.5" data-id="element-165">
                      <MessageSquare className="w-3.5 h-3.5" data-id="element-166" /> Feedback
                      Compleetheid
                    </h5>
                    <div className="flex flex-wrap gap-2" data-id="element-167">
                      <Badge variant={fullSelectedQuestion.feedback?.explanationText ? 'success' : 'danger'} size="sm" data-id="element-168">
                        {fullSelectedQuestion.feedback?.explanationText ? <CheckCircle2 className="w-3 h-3 mr-1 inline" data-id="element-169" /> : <XCircle className="w-3 h-3 mr-1 inline" data-id="element-170" />}
                        Uitleg tekst
                      </Badge>
                      <Badge variant={fullSelectedQuestion.feedback?.solutionSteps?.length ? 'success' : 'warning'} size="sm" data-id="element-171">
                        {fullSelectedQuestion.feedback?.solutionSteps?.length ? <CheckCircle2 className="w-3 h-3 mr-1 inline" data-id="element-172" /> : <XCircle className="w-3 h-3 mr-1 inline" data-id="element-173" />}
                        Stappenplan
                      </Badge>
                      <Badge variant={fullSelectedQuestion.feedback?.distractorExplanations && Object.keys(fullSelectedQuestion.feedback.distractorExplanations).length > 0 ? 'success' : 'warning'} size="sm" data-id="element-174">
                        {fullSelectedQuestion.feedback?.distractorExplanations && Object.keys(fullSelectedQuestion.feedback.distractorExplanations).length > 0 ? <CheckCircle2 className="w-3 h-3 mr-1 inline" data-id="element-175" /> : <XCircle className="w-3 h-3 mr-1 inline" data-id="element-176" />}
                        Fout-specifieke feedback
                      </Badge>
                    </div>
                  </div>
                </div>
              </div>
            </div> : <div className="flex-1 flex items-center justify-center text-gray-400" data-id="element-177">
              <div className="text-center" data-id="element-178">
                <Eye className="w-12 h-12 mx-auto mb-3 opacity-20" data-id="element-179" />
                <p className="font-medium" data-id="element-180">Selecteer een vraag</p>
                <p className="text-sm mt-1" data-id="element-181">
                  Kies een vraag uit de lijst om de preview te bekijken
                </p>
              </div>
            </div>}
        </div>
      </div>
    </div>;
  const renderTypes = () => {
    if (selectedType) {
      const typeInfo = typesList.find(t => t.type === selectedType);
      return <div className="flex flex-col h-[calc(100vh-12rem)] bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm" data-id="element-182">
          <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gray-50 flex-shrink-0" data-id="element-183">
            <div className="flex items-center gap-4" data-id="element-184">
              <Button variant="secondary" size="sm" onClick={() => {
              setSelectedType(null);
              setSelectedQuestionId(null);
            }} data-id="element-185">
                <ChevronLeft className="w-4 h-4 mr-1" data-id="element-186" /> Terug
              </Button>
              <div data-id="element-187">
                <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2" data-id="element-188">
                  {typeInfo?.label || selectedType}
                  <code className="text-xs font-mono font-normal text-gray-500 bg-gray-200 px-1.5 py-0.5 rounded" data-id="element-189">
                    {selectedType}
                  </code>
                </h2>
              </div>
            </div>
            <div className="flex items-center gap-3" data-id="element-190">
              <span className="text-sm text-gray-500" data-id="element-191">
                {typeFamilies.length} families
              </span>
              <Badge variant="info" data-id="element-192">{typeQuestions.length} vragen</Badge>
            </div>
          </div>
          <div className="flex flex-1 min-h-0" data-id="element-193">
            <div className="w-1/3 border-r border-gray-200 flex flex-col bg-white overflow-y-auto" data-id="element-194">
              {/* Group by family in the sidebar */}
              {typeFamilies.map(familyId => {
              const familyQuestions = typeQuestions.filter(q => q.familyId === familyId);
              return <div key={familyId} className="border-b border-gray-100" data-id="element-195">
                    <div className="px-4 py-2 bg-gray-50 border-b border-gray-100 flex items-center justify-between sticky top-0 z-10" data-id="element-196">
                      <span className="text-xs font-mono font-bold text-gray-700" data-id="element-197">
                        {familyId}
                      </span>
                      <span className="text-xs text-gray-500" data-id="element-198">
                        {familyQuestions.length} vragen
                      </span>
                    </div>
                    <div data-id="element-199">
                      {familyQuestions.map(q => <button key={q.id} onClick={() => setSelectedQuestionId(q.id)} className={`w-full text-left p-3 hover:bg-gray-50 transition-colors ${selectedQuestionId === q.id ? 'bg-blue-50 border-l-4 border-l-blue-500' : 'border-l-4 border-l-transparent'}`} data-id="element-200">
                          <div className="flex items-center gap-2 mb-1" data-id="element-201">
                            <span className="text-[10px] font-mono font-semibold text-gray-500" data-id="element-202">
                              {q.id.split('-').pop()}
                            </span>
                            <Badge variant="neutral" size="sm" data-id="element-203">
                              Tier {q.tier}
                            </Badge>
                          </div>
                          <p className="text-xs text-gray-800 line-clamp-2 leading-snug" data-id="element-204">
                            {q.question}
                          </p>
                        </button>)}
                    </div>
                  </div>;
            })}
            </div>
            <div className="w-2/3 bg-slate-100 flex flex-col relative" data-id="element-205">
              {fullSelectedQuestion ? <div className="flex-1 overflow-y-auto p-6 md:p-8" data-id="element-206">
                  <div className="max-w-3xl mx-auto" data-id="element-207">
                    <QuestionCardClean question={fullSelectedQuestion} data-id="element-208">
                      <QuestionRendererFactory question={fullSelectedQuestion} onAnswer={() => {}} data-id="element-209" />
                    </QuestionCardClean>
                  </div>
                </div> : <div className="flex-1 flex items-center justify-center text-gray-400" data-id="element-210">
                  <div className="text-center" data-id="element-211">
                    <Eye className="w-12 h-12 mx-auto mb-3 opacity-20" data-id="element-212" />
                    <p className="font-medium" data-id="element-213">Selecteer een vraag</p>
                  </div>
                </div>}
            </div>
          </div>
        </div>;
    }
    return <div className="space-y-6" data-id="element-214">
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-id="element-215">
          <div className="px-5 py-4 border-b border-gray-100 bg-gray-50 flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-id="element-216">
            <div data-id="element-217">
              <h3 className="font-semibold text-gray-900 flex items-center gap-2" data-id="element-218">
                <Layers className="w-5 h-5 text-blue-500" data-id="element-219" />
                Vraagtypen Overzicht ({typesList.length})
              </h3>
              <p className="text-sm text-gray-500 mt-1" data-id="element-220">
                Alle geregistreerde vraagtypen en hun huidige gebruik in de
                vragenbank.
              </p>
            </div>
            <div className="relative w-full sm:w-64" data-id="element-221">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" data-id="element-222" />
              <input type="text" placeholder="Zoek vraagtype..." value={typeSearchQuery} onChange={e => setTypeSearchQuery(e.target.value)} className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none" data-id="element-223" />
            </div>
          </div>

          {/* Distribution Bar */}
          <div className="px-5 pt-5 pb-2" data-id="element-224">
            <div className="flex items-center justify-between mb-2" data-id="element-225">
              <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-226">
                Verdeling ({totalQuestions} vragen)
              </span>
            </div>
            <div className="h-4 bg-gray-100 rounded-full overflow-hidden flex" data-id="element-227">
              {typesList.map((t, i) => {
              if (t.count === 0) return null;
              const colors = ['bg-blue-500', 'bg-indigo-500', 'bg-purple-500', 'bg-pink-500', 'bg-rose-500', 'bg-orange-500', 'bg-amber-500', 'bg-emerald-500', 'bg-teal-500', 'bg-cyan-500'];
              return <div key={t.type} className={`h-full ${colors[i % colors.length]}`} style={{
                width: `${t.usagePct}%`
              }} title={`${t.label}: ${t.count} (${t.usagePct}%)`} data-id="element-228" />;
            })}
            </div>
          </div>

          <div className="p-5" data-id="element-229">
            {filteredTypesList.length === 0 ? <div className="text-center py-8 text-gray-500" data-id="element-230">
                Geen vraagtypen gevonden.
              </div> : <motion.div variants={containerVariants} initial="hidden" animate="show" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-231">
                {filteredTypesList.map(t => {
              const Icon = getTypeIcon(t.type);
              const exampleQ = ALL_QUESTIONS_CORE.find(q => q.questionType === t.type)?.question || 'Geen voorbeeld beschikbaar';
              return <motion.div variants={itemVariants} key={t.type} data-id="element-232">
                      <Card className="p-6 flex flex-col h-full border border-gray-200 shadow-md hover:shadow-lg transition-all hover:border-blue-300 group" data-id="element-233">
                        <div className="flex justify-between items-start mb-4" data-id="element-234">
                          <div className="flex items-center gap-3" data-id="element-235">
                            <div className="w-12 h-12 rounded-xl bg-blue-50 flex items-center justify-center text-blue-600 group-hover:scale-110 transition-transform" data-id="element-236">
                              <Icon className="w-6 h-6" data-id="element-237" />
                            </div>
                            <div data-id="element-238">
                              <h3 className="font-bold text-gray-900 text-lg" data-id="element-239">
                                {t.label}
                              </h3>
                              <code className="text-xs text-gray-500 font-mono bg-gray-100 px-1.5 py-0.5 rounded" data-id="element-240">
                                {t.type}
                              </code>
                            </div>
                          </div>
                          <div className="text-right" data-id="element-241">
                            <div className={`text-2xl font-black ${t.count > 0 ? 'text-blue-600' : 'text-gray-300'}`} data-id="element-242">
                              {t.count}
                            </div>
                            <div className="text-[10px] text-gray-400 font-medium" data-id="element-243">
                              {t.usagePct}% van totaal
                            </div>
                          </div>
                        </div>

                        <div className="bg-gray-50 rounded-lg p-3 mb-4 flex-1 border border-gray-100" data-id="element-244">
                          <p className="text-xs text-gray-500 font-medium mb-1 uppercase tracking-wider" data-id="element-245">
                            Voorbeeldvraag
                          </p>
                          <p className="text-sm text-gray-800 line-clamp-2 italic" data-id="element-246">
                            "{exampleQ}"
                          </p>
                        </div>

                        <div className="mt-auto space-y-4" data-id="element-247">
                          <div className="flex flex-wrap gap-2" data-id="element-248">
                            {t.isInteractive ? <Badge variant="info" size="sm" data-id="element-249">
                                <MousePointer className="w-3 h-3 mr-1 inline" data-id="element-250" />{' '}
                                Interactief
                              </Badge> : <Badge variant="neutral" size="sm" data-id="element-251">
                                Statisch
                              </Badge>}
                            {t.isCitoEligible && <Badge variant="success" size="sm" data-id="element-252">
                                <Zap className="w-3 h-3 mr-1 inline" data-id="element-253" /> CITO
                              </Badge>}
                            {t.hasBuiltInVisuals && <Badge variant="warning" size="sm" data-id="element-254">
                                <ImageIcon className="w-3 h-3 mr-1 inline" data-id="element-255" />{' '}
                                Visuals
                              </Badge>}
                          </div>

                          <Button variant="secondary" className="w-full justify-center" onClick={() => setSelectedType(t.type)} disabled={t.count === 0} data-id="element-256">
                            <Eye className="w-4 h-4 mr-2" data-id="element-257" />
                            Bekijk {t.count} vragen
                          </Button>
                        </div>
                      </Card>
                    </motion.div>;
            })}
              </motion.div>}
          </div>
        </div>
      </div>;
  };
  const renderVisuals = () => {
    if (selectedVisual) {
      const usage = visualUsage[selectedVisual];
      return <div className="flex flex-col h-[calc(100vh-12rem)] bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm" data-id="element-258">
          <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gray-50 flex-shrink-0" data-id="element-259">
            <div className="flex items-center gap-4" data-id="element-260">
              <Button variant="secondary" size="sm" onClick={() => {
              setSelectedVisual(null);
              setSelectedQuestionId(null);
            }} data-id="element-261">
                <ChevronLeft className="w-4 h-4 mr-1" data-id="element-262" /> Terug
              </Button>
              <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2" data-id="element-263">
                <ImageIcon className="w-5 h-5 text-purple-500" data-id="element-264" />
                <code className="font-mono" data-id="element-265">{selectedVisual}</code>
              </h2>
            </div>
            <Badge variant="info" data-id="element-266">{usage?.questions.length || 0} vragen</Badge>
          </div>
          <div className="flex flex-1 min-h-0" data-id="element-267">
            <div className="w-1/3 border-r border-gray-200 flex flex-col bg-white overflow-y-auto" data-id="element-268">
              {usage?.questions.map(q => <button key={q.id} onClick={() => setSelectedQuestionId(q.id)} className={`w-full text-left p-4 border-b border-gray-100 hover:bg-gray-50 transition-colors ${selectedQuestionId === q.id ? 'bg-purple-50 border-l-4 border-l-purple-500' : 'border-l-4 border-l-transparent'}`} data-id="element-269">
                  <div className="flex items-center gap-2 mb-1.5" data-id="element-270">
                    <span className="text-xs font-mono font-semibold text-gray-600 bg-gray-100 px-1.5 py-0.5 rounded" data-id="element-271">
                      {q.id}
                    </span>
                    <Badge variant="neutral" size="sm" data-id="element-272">
                      Tier {q.tier}
                    </Badge>
                  </div>
                  <p className="text-sm text-gray-800 line-clamp-2 leading-snug" data-id="element-273">
                    {q.question}
                  </p>
                </button>)}
            </div>
            <div className="w-2/3 bg-slate-100 flex flex-col relative" data-id="element-274">
              {fullSelectedQuestion ? <div className="flex-1 overflow-y-auto p-6 md:p-8" data-id="element-275">
                  <div className="max-w-3xl mx-auto" data-id="element-276">
                    <QuestionCardClean question={fullSelectedQuestion} data-id="element-277">
                      <QuestionRendererFactory question={fullSelectedQuestion} onAnswer={() => {}} data-id="element-278" />
                    </QuestionCardClean>
                  </div>
                </div> : <div className="flex-1 flex items-center justify-center text-gray-400" data-id="element-279">
                  <div className="text-center" data-id="element-280">
                    <Eye className="w-12 h-12 mx-auto mb-3 opacity-20" data-id="element-281" />
                    <p className="font-medium" data-id="element-282">Selecteer een vraag</p>
                  </div>
                </div>}
            </div>
          </div>
        </div>;
    }
    return <div className="space-y-6" data-id="element-283">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-id="element-284">
          <StatCard label="Geregistreerde Visuals" value={registeredVisuals.length} icon={<ComponentIcon className="w-5 h-5" data-id="element-286" />} color="purple" data-id="element-285" />
          <StatCard label="Actief in Gebruik" value={sortedVisuals.filter(v => visualUsage[v].count > 0).length} icon={<CheckCircle2 className="w-5 h-5" data-id="element-288" />} color="emerald" data-id="element-287" />
          <StatCard label="Totale Referenties" value={Object.values(visualUsage).reduce((sum, u) => sum + u.count, 0)} icon={<Hash className="w-5 h-5" data-id="element-290" />} color="blue" data-id="element-289" />
        </div>

        {/* Hero Showcase Grid */}
        <div className="mb-8" data-id="element-291">
          <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2" data-id="element-292">
            <ImageIcon className="w-5 h-5 text-purple-500" data-id="element-293" />
            Visuele Componenten Showcase
          </h3>
          <motion.div variants={containerVariants} initial="hidden" animate="show" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-294">
            {sortedVisuals.map(v => {
            const usage = visualUsage[v];
            const isUsed = usage.count > 0;
            const Icon = VISUAL_ICONS[v] || ImageIcon;
            return <motion.div variants={itemVariants} key={v} data-id="element-295">
                  <Card className={`p-6 h-full flex flex-col transition-all duration-200 ${isUsed ? 'hover:shadow-lg hover:border-purple-300 cursor-pointer border-gray-200' : 'opacity-60 border-gray-100 bg-gray-50'}`} onClick={() => isUsed && setSelectedVisual(v)} data-id="element-296">
                    <div className="flex items-start justify-between mb-4" data-id="element-297">
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${isUsed ? 'bg-purple-100 text-purple-600' : 'bg-gray-200 text-gray-400'}`} data-id="element-298">
                        <Icon className="w-6 h-6" data-id="element-299" />
                      </div>
                      <div className="text-right" data-id="element-300">
                        <div className={`text-2xl font-black ${isUsed ? 'text-purple-600' : 'text-gray-400'}`} data-id="element-301">
                          {usage.count}
                        </div>
                        <div className="text-xs text-gray-500 font-medium" data-id="element-302">
                          gebruik
                        </div>
                      </div>
                    </div>

                    <div className="mb-4" data-id="element-303">
                      <code className="text-sm font-mono font-bold text-gray-900 block mb-2" data-id="element-304">
                        {v}
                      </code>
                      {isUsed && <div className="space-y-1" data-id="element-305">
                          <div className="flex justify-between text-xs text-gray-500" data-id="element-306">
                            <span data-id="element-307">Stimulus ({usage.stimulusCount})</span>
                            <span data-id="element-308">Support ({usage.supportCount})</span>
                          </div>
                          <div className="h-1.5 w-full bg-gray-100 rounded-full overflow-hidden flex" data-id="element-309">
                            <div className="bg-blue-400 h-full" style={{
                        width: `${usage.stimulusCount / usage.count * 100}%`
                      }} data-id="element-310" />
                            <div className="bg-amber-400 h-full" style={{
                        width: `${usage.supportCount / usage.count * 100}%`
                      }} data-id="element-311" />
                          </div>
                        </div>}
                    </div>

                    <div className="mt-auto pt-4 border-t border-gray-100" data-id="element-312">
                      <div className="flex flex-wrap gap-2" data-id="element-313">
                        {usage.modules.size > 0 ? Array.from(usage.modules).sort().map(m => {
                      const meta = SUBJECT_META[m];
                      const colorClass = meta?.color || 'bg-gray-500';
                      // Extract color name from bg-color-500
                      const colorName = colorClass.replace('bg-', '').replace('-500', '');
                      return <span key={m} className={`text-[10px] font-bold px-2 py-1 rounded-md border bg-gradient-to-br from-${colorName}-50 to-${colorName}-100 border-${colorName}-200 text-${colorName}-700`} title={meta?.name || m} data-id="element-314">
                                  {m}
                                </span>;
                    }) : <span className="text-xs text-gray-400 italic" data-id="element-315">
                            Niet in gebruik
                          </span>}
                      </div>
                    </div>
                  </Card>
                </motion.div>;
          })}
          </motion.div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-id="element-316">
          <div className="px-5 py-4 border-b border-gray-100 bg-gray-50" data-id="element-317">
            <h3 className="font-semibold text-gray-900 flex items-center gap-2" data-id="element-318">
              <ImageIcon className="w-5 h-5 text-purple-500" data-id="element-319" />
              Visuele Componenten Gebruik
            </h3>
            <p className="text-sm text-gray-500 mt-1" data-id="element-320">
              Overzicht van welke componenten worden gebruikt als stimulus
              (tijdens de vraag) of support (in de feedback).
            </p>
          </div>
          <div className="divide-y divide-gray-100" data-id="element-321">
            {sortedVisuals.map(v => {
            const usage = visualUsage[v];
            const usagePct = maxVisualUsage > 0 ? usage.count / maxVisualUsage * 100 : 0;
            return <div key={v} onClick={() => usage.count > 0 && setSelectedVisual(v)} className={`p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4 transition-colors ${usage.count > 0 ? 'hover:bg-purple-50 cursor-pointer' : 'opacity-60'}`} data-id="element-322">
                  <div className="flex items-center gap-4 w-full sm:w-1/3" data-id="element-323">
                    <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center flex-shrink-0" data-id="element-324">
                      <ImageIcon className="w-5 h-5 text-purple-600" data-id="element-325" />
                    </div>
                    <div className="min-w-0" data-id="element-326">
                      <code className="text-sm font-mono font-bold text-gray-800 truncate block" data-id="element-327">
                        {v}
                      </code>
                      <div className="text-xs text-gray-500 mt-0.5 flex items-center gap-2" data-id="element-328">
                        <span data-id="element-329">{usage.count}x gebruikt</span>
                        {usage.count > 0 && <>
                            <span data-id="element-330">·</span>
                            <span className="text-blue-600" data-id="element-331">
                              {usage.stimulusCount} stimulus
                            </span>
                            <span data-id="element-332">·</span>
                            <span className="text-amber-600" data-id="element-333">
                              {usage.supportCount} support
                            </span>
                          </>}
                      </div>
                    </div>
                  </div>

                  <div className="w-full sm:w-1/3 flex items-center" data-id="element-334">
                    <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden flex" data-id="element-335">
                      <div className="bg-blue-400 h-full" style={{
                    width: `${maxVisualUsage > 0 ? usage.stimulusCount / maxVisualUsage * 100 : 0}%`
                  }} data-id="element-336" />
                      <div className="bg-amber-400 h-full" style={{
                    width: `${maxVisualUsage > 0 ? usage.supportCount / maxVisualUsage * 100 : 0}%`
                  }} data-id="element-337" />
                    </div>
                  </div>

                  <div className="w-full sm:w-1/3 flex flex-wrap gap-1.5 justify-end" data-id="element-338">
                    {usage.modules.size > 0 ? Array.from(usage.modules).sort().map(m => {
                  const meta = SUBJECT_META[m];
                  return <div key={m} className="flex items-center gap-1 px-2 py-1 bg-gray-50 border border-gray-200 rounded text-xs font-medium text-gray-600" title={meta?.name || m} data-id="element-339">
                              <div className={`w-2 h-2 rounded-full ${meta?.color || 'bg-gray-400'}`} data-id="element-340" />
                              {m}
                            </div>;
                }) : <Badge variant="neutral" size="sm" data-id="element-341">
                        Ongebruikt
                      </Badge>}
                  </div>
                </div>;
          })}
          </div>
        </div>
      </div>;
  };
  const renderFamilies = () => <div className="space-y-6" data-id="element-342">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-343">
        <StatCard label="Totaal Families" value={totalFamiliesCount} icon={<Tag className="w-5 h-5" data-id="element-345" />} color="blue" data-id="element-344" />
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 flex items-center" data-id="element-346">
          <div className="relative w-full" data-id="element-347">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" data-id="element-348" />
            <input type="text" placeholder="Zoek op familyId..." value={familySearchQuery} onChange={e => setFamilySearchQuery(e.target.value)} className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none" data-id="element-349" />
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-id="element-350">
        {Object.entries(familiesBySubject).map(([subjectCode, families]) => {
        const meta = SUBJECT_META[subjectCode];
        const isExpanded = expandedSubjects.has(subjectCode);
        const filteredFamilies = familySearchQuery ? families.filter(f => f.id.toLowerCase().includes(familySearchQuery.toLowerCase())) : families;
        if (filteredFamilies.length === 0) return null;
        return <div key={subjectCode} className="border-b border-gray-100 last:border-0" data-id="element-351">
              <button onClick={() => toggleSubject(subjectCode)} className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 transition-colors" data-id="element-352">
                <div className="flex items-center gap-3" data-id="element-353">
                  {isExpanded ? <ChevronDown className="w-4 h-4 text-gray-400" data-id="element-354" /> : <ChevronRight className="w-4 h-4 text-gray-400" data-id="element-355" />}
                  <div className={`w-3 h-3 rounded-full ${meta?.color || 'bg-gray-400'}`} data-id="element-356" />
                  <h3 className="font-bold text-gray-900" data-id="element-357">
                    {meta?.name || subjectCode}
                  </h3>
                  <Badge variant="neutral" size="sm" data-id="element-358">
                    {filteredFamilies.length} families
                  </Badge>
                </div>
              </button>

              {isExpanded && <div className="divide-y divide-gray-50" data-id="element-359">
                  {filteredFamilies.map(family => <div key={family.id} className="p-4 pl-11 flex flex-col sm:flex-row sm:items-center justify-between gap-4 hover:bg-blue-50/30 transition-colors" data-id="element-360">
                      <div data-id="element-361">
                        <code className="text-sm font-mono font-bold text-gray-800" data-id="element-362">
                          {family.id}
                        </code>
                        <div className="text-xs text-gray-500 mt-1 flex items-center gap-3" data-id="element-363">
                          <span data-id="element-364">{family.questionCount} vragen</span>
                          <span data-id="element-365">·</span>
                          <span data-id="element-366">
                            Tiers:{' '}
                            {Array.from(family.tiers).sort((a, b) => a - b).join(', ')}
                          </span>
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-1.5 justify-start sm:justify-end" data-id="element-367">
                        {Array.from(family.types).map(type => <Badge key={type} variant="info" size="sm" data-id="element-368">
                            {type}
                          </Badge>)}
                      </div>
                    </div>)}
                </div>}
            </div>;
      })}
        {Object.values(familiesBySubject).every(families => familySearchQuery ? families.filter(f => f.id.toLowerCase().includes(familySearchQuery.toLowerCase())).length === 0 : families.length === 0) && <div className="p-8 text-center text-gray-500" data-id="element-369">
            Geen families gevonden voor "{familySearchQuery}"
          </div>}
      </div>
    </div>;
  return <PageLayout title="Vragenbank" subtitle={selectedSubject ? `Vragen voor ${SUBJECT_META[selectedSubject]?.name || selectedSubject}` : 'Bladeren door vragen, typen, visuele componenten en families'} breadcrumbs={[{
    label: 'Content & Kwaliteit'
  }, {
    label: 'Vragenbank'
  }]} data-id="element-370">
      {selectedSubject ? renderSubjectBrowser() : <div className="space-y-6" data-id="element-371">
          {/* Tabs */}
          <div className="flex gap-2 border-b border-gray-200 pb-px overflow-x-auto" data-id="element-372">
            <button onClick={() => setActiveTab('overview')} className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${activeTab === 'overview' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-373">
              <div className="flex items-center gap-2" data-id="element-374">
                <Database className="w-4 h-4" data-id="element-375" />
                Vakken
              </div>
            </button>
            <button onClick={() => setActiveTab('types')} className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${activeTab === 'types' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-376">
              <div className="flex items-center gap-2" data-id="element-377">
                <Layers className="w-4 h-4" data-id="element-378" />
                Vraagtypen
              </div>
            </button>
            <button onClick={() => setActiveTab('visuals')} className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${activeTab === 'visuals' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-379">
              <div className="flex items-center gap-2" data-id="element-380">
                <ComponentIcon className="w-4 h-4" data-id="element-381" />
                Visuele Componenten
              </div>
            </button>
            <button onClick={() => setActiveTab('families')} className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${activeTab === 'families' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-382">
              <div className="flex items-center gap-2" data-id="element-383">
                <Tag className="w-4 h-4" data-id="element-384" />
                Families
              </div>
            </button>
          </div>

          {activeTab === 'overview' && renderOverview()}
          {activeTab === 'types' && renderTypes()}
          {activeTab === 'visuals' && renderVisuals()}
          {activeTab === 'families' && renderFamilies()}
        </div>}
    </PageLayout>;
}